import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useToast } from '../../../hooks/toast';
import { useAuth } from '../../../hooks/auth';
import { Screens } from '../../../shared/enums/screens.enum';
import { PermissionActions } from '../../../shared/enums/permissions.enum';
import {
  AButton,
  ASelect,
  ATable,
  ATitle,
  DropdownOptions,
  MenuOptions,
  PageIntro,
  PageLayout,
  PagePanel,
} from '../../../components';
import {
  ExclamationCircleOutlined,
  RedoOutlined,
  WhatsAppOutlined,
  PhoneOutlined,
  MailOutlined,
  CalendarOutlined,
  UserOutlined,
  TeamOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ArrowRightOutlined,
  ReloadOutlined,
  FilterOutlined,
  ThunderboltOutlined,
  EyeOutlined,
  CheckSquareOutlined,
  ClockCircleOutlined,
  CloseOutlined,
  EditOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import {
  Badge,
  Card,
  Col,
  Divider,
  Drawer,
  Empty,
  Form,
  Input,
  Row,
  Space,
  Tag,
  Select,
  DatePicker,
  Switch,
} from 'antd';
import { Tag as AntTag } from 'antd';
import dayjs from 'dayjs';
import {
  getCrcPendencies,
  getCrcPendencyTabs,
  ICrcPendency,
  CrcPendencyPriority,
  CrcPendencyType,
  CrcPendencyStatus,
  resolveCrcPendency,
  executePendencyQuickAction,
  generateCrcPendencies,
  crcSendWhatsapp,
} from '../../../http/crc';
import { getUsers } from '../../../http/users';
import { getSelectProfessionals } from '../../../http/professionals';
import { getSelectSpecialties } from '../../../http/specialties';
import { IProfessional, ISpecialty, IUser } from '../../../shared/interfaces';

type PendencyTab =
  | 'ALL'
  | 'UNCONFIRMED'
  | 'ABSENT'
  | 'NO_RETURN'
  | 'INTERRUPTED'
  | 'POST_SERVICE';

const TAB_TO_MOTIVO: Record<Exclude<PendencyTab, 'ALL'>, CrcPendencyType> = {
  UNCONFIRMED: 'NAO_CONFIRMADO',
  ABSENT: 'FALTA',
  NO_RETURN: 'SEM_RETORNO',
  INTERRUPTED: 'TRATAMENTO_INTERROMPIDO',
  POST_SERVICE: 'POS_ATENDIMENTO',
};

const MOTIVO_LABEL: Record<CrcPendencyType, string> = {
  NAO_CONFIRMADO: 'Não confirmado',
  FALTA: 'Falta',
  SEM_RETORNO: 'Sem retorno',
  TRATAMENTO_INTERROMPIDO: 'Tratamento interrompido',
  POS_ATENDIMENTO: 'Pós-atendimento',
};

const TABS: {
  value: PendencyTab;
  label: string;
  icon: React.ReactNode;
}[] = [
  { value: 'ALL', label: 'Todas', icon: <ExclamationCircleOutlined /> },
  { value: 'UNCONFIRMED', label: 'Não Confirmados', icon: <ClockCircleOutlined /> },
  { value: 'ABSENT', label: 'Faltas', icon: <CloseCircleOutlined /> },
  { value: 'NO_RETURN', label: 'Sem Retorno', icon: <ClockCircleOutlined /> },
  { value: 'INTERRUPTED', label: 'Tratamento Interrompido', icon: <CloseCircleOutlined /> },
  { value: 'POST_SERVICE', label: 'Pós-atendimento', icon: <CheckCircleOutlined /> },
];

const PRIORITY_COLORS: Record<CrcPendencyPriority, { bg: string; tag: string; label: string }> = {
  BAIXA: { bg: 'green', tag: 'success', label: 'Baixa' },
  MEDIA: { bg: 'gold', tag: 'warning', label: 'Média' },
  ALTA: { bg: 'red', tag: 'error', label: 'Alta' },
};

const TITLE_BY_TAB: Record<PendencyTab, string> = {
  ALL: 'Todas as pendências',
  UNCONFIRMED: 'Consultas não confirmadas',
  ABSENT: 'Pacientes faltosos',
  NO_RETURN: 'Sem retorno há mais de 30 dias',
  INTERRUPTED: 'Tratamentos interrompidos',
  POST_SERVICE: 'Pós-atendimento a contatar',
};

const SITUACAO_TAG_COLOR: Record<
  CrcPendencyStatus,
  'success' | 'warning' | 'error' | 'default' | 'processing'
> = {
  ABERTA: 'processing',
  RESOLVIDA: 'success',
  IGNORADA: 'default',
};

const formatPhoneDisplay = (val?: string | null): string => {
  if (!val) return '-';
  const digits = String(val).replace(/\D/g, '');
  if (digits.length === 13 && digits.startsWith('55')) {
    return `(${digits.slice(2, 4)}) ${digits.slice(4, 9)}-${digits.slice(9, 13)}`;
  }
  if (digits.length === 11) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7, 11)}`;
  }
  if (digits.length === 10) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6, 10)}`;
  }
  return val;
};

const CrcPendencies: React.FC = () => {
  const { addToast } = useToast();
  const { screenFind, permissionFind } = useAuth();

  const [loading, setLoading] = useState<boolean>(false);
  const [tab, setTab] = useState<PendencyTab>('ALL');

  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);
  const [pages, setPages] = useState<number>(0);

  const [pendencies, setPendencies] = useState<ICrcPendency[]>([]);
  const [users, setUsers] = useState<IUser[]>([]);
  const [professionals, setProfessionals] = useState<IProfessional[]>([]);
  const [specialties, setSpecialties] = useState<ISpecialty[]>([]);

  const [filterUserId, setFilterUserId] = useState<string | undefined>();
  const [filterProfessionalId, setFilterProfessionalId] = useState<string | undefined>();
  const [filterSpecialtyId, setFilterSpecialtyId] = useState<string | undefined>();

  const [selectedPendency, setSelectedPendency] = useState<ICrcPendency | null>(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [tabsLoaded, setTabsLoaded] = useState<boolean>(false);

  const [counts, setCounts] = useState<Record<PendencyTab, number>>({
    ALL: 0,
    UNCONFIRMED: 0,
    ABSENT: 0,
    NO_RETURN: 0,
    INTERRUPTED: 0,
    POST_SERVICE: 0,
  });

  const [form] = Form.useForm();
  const [reloadKey, setReloadKey] = useState<number>(0);

  const canResolve = permissionFind([PermissionActions.crcPendenciesResolve]);
  const canCreate = permissionFind([PermissionActions.crcContactsCreate]);

  const loadTabs = useCallback(async () => {
    try {
      const res = await getCrcPendencyTabs();
      setCounts({
        ALL: Number(res.all ?? 0),
        UNCONFIRMED: Number(res.unconfirmed ?? 0),
        ABSENT: Number(res.absent ?? 0),
        NO_RETURN: Number(res.no_return ?? 0),
        INTERRUPTED: Number(res.interrupted ?? 0),
        POST_SERVICE: Number(res.post_service ?? 0),
      });
      setTabsLoaded(true);
    } catch {
      setTabsLoaded(false);
    }
  }, []);

  const loadUsers = useCallback(async () => {
    try {
      const res = await getUsers(1000, 0);
      setUsers(res.data ?? []);
    } catch {
      setUsers([]);
    }
  }, []);

  const loadProfessionals = useCallback(async () => {
    try {
      const data = await getSelectProfessionals();
      setProfessionals(data ?? []);
    } catch {
      setProfessionals([]);
    }
  }, []);

  const loadSpecialties = useCallback(async () => {
    try {
      const data = await getSelectSpecialties();
      setSpecialties(data ?? []);
    } catch {
      setSpecialties([]);
    }
  }, []);

  const loadPendencies = useCallback(async () => {
    setLoading(true);
    try {
      const motivo = tab === 'ALL' ? undefined : TAB_TO_MOTIVO[tab];
      const res = await getCrcPendencies({
        limit,
        offset: (page - 1) * limit,
        motivo,
        assigned_user_id: filterUserId,
        professional_id: filterProfessionalId,
        specialty_id: filterSpecialtyId,
      });
      setPendencies(res.data ?? []);
      setPages(res.count ?? 0);
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Falha ao carregar pendências';
      addToast({ type: 'error', title: 'Erro ao carregar pendências', description: msg });
      setPendencies([]);
      setPages(0);
    } finally {
      setLoading(false);
    }
  }, [limit, page, filterUserId, filterProfessionalId, filterSpecialtyId, tab, addToast]);

  useEffect(() => {
    loadUsers();
    loadProfessionals();
    loadSpecialties();
    loadTabs();
  }, [loadUsers, loadProfessionals, loadSpecialties, loadTabs]);

  useEffect(() => {
    setPage(1);
    loadPendencies();
    loadTabs();
  }, [tab, filterUserId, filterProfessionalId, filterSpecialtyId, reloadKey, loadPendencies, loadTabs]);

  useEffect(() => {
    loadPendencies();
  }, [page, limit, loadPendencies]);

  const renderPriorityTag = (p?: CrcPendencyPriority | null) => {
    if (!p) return <Tag>—</Tag>;
    const info = PRIORITY_COLORS[p] || PRIORITY_COLORS.MEDIA;
    return <Tag color={info.tag}>{info.label}</Tag>;
  };

  const quickWhatsApp = async (pendency?: ICrcPendency) => {
    const target = pendency ?? selectedPendency;
    if (!target) {
      addToast({ type: 'warning', title: 'Atenção', description: 'Selecione uma pendência primeiro.' });
      return;
    }
    const phone = target.phone;
    if (!phone) {
      addToast({ type: 'error', title: 'Atenção', description: 'Paciente sem telefone cadastrado.' });
      return;
    }
    try {
      await executePendencyQuickAction(target.id, {
        acao: 'WHATSAPP',
      });
    } catch {
      // noop
    }
    try {
      await crcSendWhatsapp({
        phone: phone.replace(/\D/g, ''),
        message: `Olá ${target.client_name || ''}! Passando para lembrá-lo(a) do seu retorno. Por favor, entre em contato para agendarmos.`,
      });
    } catch {
      // noop
    }
    addToast({
      type: 'success',
      title: 'WhatsApp',
      description: `Abrindo / enfileirando WhatsApp para ${target.client_name ?? '-'}...`,
    });
    setReloadKey((k) => k + 1);
  };

  const quickCall = async (pendency?: ICrcPendency) => {
    const target = pendency ?? selectedPendency;
    if (!target) {
      addToast({ type: 'warning', title: 'Atenção', description: 'Selecione uma pendência primeiro.' });
      return;
    }
    try {
      await executePendencyQuickAction(target.id, { acao: 'LIGAR' });
    } catch {
      // noop
    }
    addToast({
      type: 'info',
      title: 'Ligação',
      description: `Contato registrado para ${target.client_name ?? '-'}`,
    });
    setReloadKey((k) => k + 1);
  };

  const quickEmail = async (pendency?: ICrcPendency) => {
    const target = pendency ?? selectedPendency;
    if (!target) {
      addToast({ type: 'warning', title: 'Atenção', description: 'Selecione uma pendência primeiro.' });
      return;
    }
    try {
      await executePendencyQuickAction(target.id, { acao: 'EMAIL' });
    } catch {
      // noop
    }
    addToast({
      type: 'info',
      title: 'E-mail',
      description: `Contato registrado (e-mail) para ${target.client_name ?? '-'}`,
    });
    setReloadKey((k) => k + 1);
  };

  const quickSchedule = async (pendency?: ICrcPendency) => {
    const target = pendency ?? selectedPendency;
    if (!target) {
      addToast({ type: 'warning', title: 'Atenção', description: 'Selecione uma pendência primeiro.' });
      return;
    }
    try {
      await executePendencyQuickAction(target.id, { acao: 'AGENDAR_RETORNO' });
    } catch {
      // noop
    }
    addToast({
      type: 'info',
      title: 'Agendar Retorno',
      description: `Encaminhando para agenda (${target.client_name ?? '-'})...`,
    });
  };

  const handleResolve = async (p: ICrcPendency, situacao: CrcPendencyStatus = 'RESOLVIDA') => {
    try {
      await resolveCrcPendency(p.id, {
        observacoes: `Resolvido via ações rápidas em ${new Date().toLocaleString('pt-BR')}`,
        situacao,
      });
      addToast({
        type: 'success',
        title: 'Pendência Atualizada',
        description: `${p.client_name ?? '-'} marcado(a) como ${situacao === 'RESOLVIDA' ? 'resolvido(a)' : 'ignorado(a)'}.`,
      });
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Erro ao resolver';
      addToast({ type: 'error', title: 'Erro', description: msg });
      return;
    }
    setReloadKey((k) => k + 1);
  };

  const handleGenerate = async () => {
    try {
      const res = await generateCrcPendencies();
      addToast({
        type: 'success',
        title: 'Pendências geradas',
        description: `Criadas ${res?.generated ?? 0} novas pendências. ${res?.already_exists ? res.already_exists + ' já existentes.' : ''}`,
      });
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Erro';
      addToast({ type: 'error', title: 'Erro ao gerar', description: msg });
    }
    setReloadKey((k) => k + 1);
  };

  const openDrawer = (p: ICrcPendency) => {
    setSelectedPendency(p);
    form.setFieldsValue({
      client_name: p.client_name,
      phone: p.phone,
      email: p.email,
      motivo: p.motivo,
      prioridade: p.prioridade,
      situacao: p.situacao,
      acao_recomendada: p.acao_recomendada,
      observacoes: p.observacoes,
      ultimo_contato: p.ultimo_contato ? dayjs(p.ultimo_contato) : null,
      dias_contato: p.dias_contato,
    });
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
  };

  const handleSaveDrawer = async (values: any) => {
    if (!selectedPendency) return;
    setSaving(true);
    try {
      const situacao = values?.situacao as CrcPendencyStatus;
      const isResolve = situacao === 'RESOLVIDA' || situacao === 'IGNORADA';
      if (isResolve) {
        await resolveCrcPendency(selectedPendency.id, {
          situacao,
          observacoes: values?.observacoes || undefined,
        });
      } else {
        await resolveCrcPendency(selectedPendency.id, {
          observacoes: values?.observacoes || undefined,
          situacao: 'ABERTA',
        });
      }
      addToast({ type: 'success', title: 'Alterações salvas', description: 'Pendência atualizada com sucesso.' });
      closeDrawer();
      setReloadKey((k) => k + 1);
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Erro ao salvar';
      addToast({ type: 'error', title: 'Erro', description: msg });
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    {
      title: 'Paciente',
      dataIndex: 'client_name',
      width: 220,
      fixed: 'left' as const,
      render: (v: string | undefined | null, rec: ICrcPendency) => (
        <Space direction="vertical" size={0}>
          <Space size={6}>
            <UserOutlined style={{ color: '#1677ff' }} />
            <span style={{ fontWeight: 600 }}>{v ?? '-'}</span>
          </Space>
          <Space size={8} style={{ fontSize: 11, color: '#888' }}>
            <span><PhoneOutlined /> {formatPhoneDisplay(rec.phone)}</span>
            {rec.email && <span><MailOutlined /> {rec.email}</span>}
          </Space>
        </Space>
      ),
    },
    {
      title: 'Motivo',
      dataIndex: 'motivo',
      width: 180,
      render: (v: CrcPendencyType, rec: ICrcPendency) => (
        <Space direction="vertical" size={2}>
          <Tag color="blue">{MOTIVO_LABEL[v] || v}</Tag>
          {rec.acao_recomendada && (
            <span style={{ fontSize: 11, color: '#666' }}>
              {rec.acao_recomendada.length > 60 ? rec.acao_recomendada.slice(0, 60) + '...' : rec.acao_recomendada}
            </span>
          )}
        </Space>
      ),
    },
    {
      title: 'Último Contato',
      width: 140,
      dataIndex: 'ultimo_contato',
      render: (v: string | undefined | null, rec: ICrcPendency) => (
        <Space direction="vertical" size={2}>
          <span>{v ? dayjs(v).format('DD/MM/YYYY') : '-'}</span>
          {Number(rec.dias_contato) > 0 && (
            <Tag color={Number(rec.dias_contato) > 5 ? 'red' : Number(rec.dias_contato) > 2 ? 'orange' : 'blue'}>
              {rec.dias_contato}d sem contato
            </Tag>
          )}
        </Space>
      ),
    },
    {
      title: 'Situação',
      width: 120,
      dataIndex: 'situacao',
      render: (v: CrcPendencyStatus) => (
        <Badge status={SITUACAO_TAG_COLOR[v] || 'default'} text={v} />
      ),
    },
    {
      title: 'Prioridade',
      width: 100,
      dataIndex: 'prioridade',
      render: (v: CrcPendencyPriority) => renderPriorityTag(v),
    },
    {
      title: 'Atendente',
      width: 160,
      render: (_v: any, rec: ICrcPendency) => (
        <Space direction="vertical" size={0}>
          <Space size={6}>
            <TeamOutlined />
            <span>{rec.assigned_name ?? rec.updated_by ?? '-'}</span>
          </Space>
          {rec.professional_name && (
            <span style={{ fontSize: 11, color: '#888' }}>
              Dentista: {rec.professional_name}
            </span>
          )}
        </Space>
      ),
    },
    {
      title: 'Ações',
      width: 280,
      fixed: 'right' as const,
      render: (cell: ICrcPendency) => (
        <Space size={2} wrap>
          <AButton
            type="text"
            size="small"
            icon={<WhatsAppOutlined />}
            title="WhatsApp"
            style={{ color: '#25D366' }}
            onClick={() => quickWhatsApp(cell)}
          />
          <AButton
            type="text"
            size="small"
            icon={<PhoneOutlined />}
            title="Ligar"
            style={{ color: '#1677ff' }}
            onClick={() => quickCall(cell)}
          />
          <AButton
            type="text"
            size="small"
            icon={<MailOutlined />}
            title="E-mail"
            style={{ color: '#fa8c16' }}
            onClick={() => quickEmail(cell)}
          />
          <AButton
            type="text"
            size="small"
            icon={<CalendarOutlined />}
            title="Agendar Retorno"
            style={{ color: '#722ed1' }}
            onClick={() => quickSchedule(cell)}
          />
          <AButton
            type="text"
            size="small"
            icon={<EditOutlined />}
            title="Editar / Atualizar"
            style={{ color: '#0958d9' }}
            onClick={() => openDrawer(cell)}
          />
          <DropdownOptions
            size="small"
            items={[
              {
                label: 'Ver Detalhes',
                key: '1',
                icon: <EyeOutlined />,
                onClick: () => openDrawer(cell),
              },
              ...(canResolve
                ? [
                    {
                      label: 'Marcar Resolvida',
                      key: '2',
                      icon: <CheckSquareOutlined />,
                      onClick: () => handleResolve(cell, 'RESOLVIDA'),
                    },
                    {
                      label: 'Ignorar Pendência',
                      key: '3',
                      icon: <CloseCircleOutlined />,
                      onClick: () => handleResolve(cell, 'IGNORADA'),
                    },
                  ]
                : []),
            ]}
          />
        </Space>
      ),
    },
  ];

  const statsSummary = useMemo(() => {
    const total = pages;
    const resolved = pendencies.filter((p) => p.situacao === 'RESOLVIDA').length;
    const high = pendencies.filter((p) => p.prioridade === 'ALTA').length;
    return { total, resolved, high };
  }, [pages, pendencies]);

  return (
    <PageLayout>
      <PageIntro
        badge="CRC"
        icon={<ExclamationCircleOutlined />}
        title="Pendências CRC"
        description="Acompanhe e resolva pendências de relacionamento: faltas, não confirmados, sem retorno, tratamentos interrompidos e pós-atendimento."
      />
      <MenuOptions
        menu_items={[
          {
            label: 'Atualizar',
            key: '1',
            icon: <RedoOutlined />,
            onClick: () => {
              loadTabs();
              loadPendencies();
            },
          },
          ...(canCreate
            ? [
                {
                  label: 'Gerar Pendências Automáticas',
                  key: '2',
                  icon: <ThunderboltOutlined />,
                  onClick: handleGenerate,
                },
              ]
            : []),
          ...(canResolve
            ? [
                {
                  label: 'Resolver Selecionados',
                  key: '3',
                  icon: <CheckSquareOutlined />,
                  onClick: () =>
                    addToast({
                      type: 'success',
                      title: 'Concluído',
                      description: `${selectedRowKeys.length} pendências(s) marcadas. Abra e resolva individualmente para observações.`,
                    }),
                },
              ]
            : []),
        ]}
      />
      <PagePanel>
        <Card bordered={false} size="small" style={{ marginBottom: 16 }}>
          <Space wrap size={[8, 8]}>
            {TABS.map((t) => (
              <AntTag.CheckableTag
                key={t.value}
                checked={tab === t.value}
                onChange={() => setTab(t.value)}
                style={{
                  padding: '6px 14px',
                  fontSize: 13,
                  borderRadius: 6,
                  border: tab === t.value ? '1px solid #1677ff' : '1px solid #d9d9d9',
                  background: tab === t.value ? '#e6f4ff' : '#fff',
                  fontWeight: tab === t.value ? 600 : 400,
                }}
              >
                <Space size={6}>
                  {t.icon}
                  {t.label}
                  <Badge
                    count={counts[t.value]}
                    showZero
                    style={{
                      backgroundColor: tab === t.value ? '#1677ff' : '#bfbfbf',
                      boxShadow: 'none',
                    }}
                  />
                </Space>
              </AntTag.CheckableTag>
            ))}
          </Space>
        </Card>
      </PagePanel>
      <PagePanel>
        <Row gutter={[16, 16]}>
          <Col xs={24} xl={18}>
            <Card
              bordered={false}
              size="small"
              title={
                <Space>
                  <FilterOutlined />
                  {TITLE_BY_TAB[tab]}
                </Space>
              }
              extra={
                <Space wrap size={[8, 8]}>
                  <ASelect
                    allowClear
                    style={{ width: 180 }}
                    placeholder="Atendente"
                    value={filterUserId}
                    onChange={(v) => setFilterUserId(v)}
                    options={users.map((u) => ({ label: u.name, value: u.id }))}
                  />
                  <ASelect
                    allowClear
                    style={{ width: 180 }}
                    placeholder="Dentista"
                    value={filterProfessionalId}
                    onChange={(v) => setFilterProfessionalId(v)}
                    options={professionals.map((p) => ({ label: p.name, value: p.id }))}
                  />
                  <ASelect
                    allowClear
                    style={{ width: 180 }}
                    placeholder="Especialidade"
                    value={filterSpecialtyId}
                    onChange={(v) => setFilterSpecialtyId(v)}
                    options={specialties.map((s) => ({ label: s.description, value: s.id }))}
                  />
                  <AButton
                    icon={<ReloadOutlined />}
                    onClick={() => {
                      setReloadKey((k) => k + 1);
                    }}
                  >
                    Filtrar
                  </AButton>
                </Space>
              }
            >
              {pendencies.length === 0 && !loading ? (
                <Empty description="Nenhuma pendência para os filtros selecionados." />
              ) : (
                <ATable
                  loading={loading}
                  scroll={{ x: 1280, y: 520 }}
                  dataSource={pendencies}
                  rowKey="id"
                  size="small"
                  rowSelection={{
                    selectedRowKeys,
                    onChange: (keys, rows) => {
                      setSelectedRowKeys(keys);
                      setSelectedPendency(
                        (rows?.[0] as ICrcPendency) ?? null,
                      );
                    },
                  }}
                  columns={columns as any}
                  page={page}
                  setPage={setPage}
                  pages={pages}
                  limit={limit}
                  setLimit={setLimit}
                  onRow={(rec: ICrcPendency) => ({
                    onClick: () => openDrawer(rec),
                    style: { cursor: 'pointer' },
                  })}
                />
              )}
            </Card>
          </Col>
          <Col xs={24} xl={6}>
            <Card
              bordered={false}
              size="small"
              style={{ position: 'sticky', top: 8 }}
              title={
                <Space>
                  <ThunderboltOutlined style={{ color: '#faad14' }} />
                  Ações Rápidas
                </Space>
              }
            >
              {!selectedPendency ? (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={
                    <span style={{ color: '#999' }}>
                      Selecione uma linha para executar ações no paciente
                    </span>
                  }
                />
              ) : (
                <>
                  <Card
                    type="inner"
                    size="small"
                    style={{ background: '#fafafa', marginBottom: 12 }}
                    title={
                      <Space>
                        <UserOutlined />
                        Paciente Selecionado
                      </Space>
                    }
                  >
                    <div style={{ fontWeight: 600, fontSize: 14 }}>
                      {selectedPendency.client_name ?? '-'}
                    </div>
                    <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                      <Space direction="vertical" size={2} style={{ width: '100%' }}>
                        <Space size={6}>
                          <PhoneOutlined />
                          {formatPhoneDisplay(selectedPendency.phone)}
                        </Space>
                        {selectedPendency.email && (
                          <Space size={6}>
                            <MailOutlined />
                            <span style={{ maxWidth: '90%' }}>{selectedPendency.email}</span>
                          </Space>
                        )}
                        <Space size={6}>
                          <ExclamationCircleOutlined />
                          {MOTIVO_LABEL[selectedPendency.motivo] || selectedPendency.motivo}
                        </Space>
                        {selectedPendency.professional_name && (
                          <Space size={6}>
                            <TeamOutlined />
                            Dentista: {selectedPendency.professional_name}
                          </Space>
                        )}
                        <Space size={6}>
                          <TeamOutlined />
                          Atendente: {selectedPendency.assigned_name ?? selectedPendency.updated_by ?? '-'}
                        </Space>
                        <Space size={6}>
                          Prioridade: {renderPriorityTag(selectedPendency.prioridade)}
                        </Space>
                      </Space>
                    </div>
                  </Card>

                  <Space direction="vertical" style={{ width: '100%' }} size={8}>
                    <AButton
                      block
                      size="large"
                      icon={<WhatsAppOutlined />}
                      style={{
                        background: '#25D366',
                        borderColor: '#25D366',
                        color: '#fff',
                      }}
                      onClick={() => quickWhatsApp()}
                    >
                      WhatsApp
                    </AButton>
                    <AButton block size="large" type="primary" icon={<PhoneOutlined />} onClick={() => quickCall()}>
                      Ligar
                    </AButton>
                    <AButton block size="large" icon={<MailOutlined />} onClick={() => quickEmail()}>
                      Enviar E-mail
                    </AButton>
                    <AButton
                      block
                      size="large"
                      icon={<CalendarOutlined />}
                      style={{
                        background: '#722ed1',
                        borderColor: '#722ed1',
                        color: '#fff',
                      }}
                      onClick={() => quickSchedule()}
                    >
                      Agendar Retorno
                    </AButton>
                    <AButton
                      block
                      size="large"
                      icon={<EditOutlined />}
                      type="primary"
                      ghost
                      onClick={() => openDrawer(selectedPendency)}
                    >
                      Editar / Atualizar Registro
                    </AButton>
                  </Space>

                  <Divider style={{ margin: '16px 0' }} />

                  {canResolve && (
                    <Space direction="vertical" style={{ width: '100%' }} size={6}>
                      <AButton
                        block
                        icon={<CheckSquareOutlined />}
                        onClick={() => handleResolve(selectedPendency, 'RESOLVIDA')}
                      >
                        Marcar como Resolvido
                      </AButton>
                      <AButton
                        block
                        icon={<CloseCircleOutlined />}
                        onClick={() => handleResolve(selectedPendency, 'IGNORADA')}
                      >
                        Ignorar Pendência
                      </AButton>
                    </Space>
                  )}
                </>
              )}
            </Card>
          </Col>
        </Row>
      </PagePanel>

      <Drawer
        title={
          <Space>
            <EditOutlined />
            Editar Pendência CRC • {selectedPendency?.client_name ?? ''}
          </Space>
        }
        placement="right"
        width={560}
        onClose={closeDrawer}
        open={drawerOpen}
        extra={
          <Space>
            <AButton onClick={closeDrawer} icon={<CloseOutlined />}>
              Fechar
            </AButton>
            <AButton
              type="primary"
              icon={<SaveOutlined />}
              loading={saving}
              onClick={() => form.submit()}
            >
              Salvar
            </AButton>
          </Space>
        }
      >
        {selectedPendency && (
          <Form form={form} layout="vertical" onFinish={handleSaveDrawer}>
            <Row gutter={12}>
              <Col xs={24}>
                <Form.Item label="Paciente" name="client_name">
                  <Input disabled prefix={<UserOutlined />} />
                </Form.Item>
              </Col>
              <Col xs={12}>
                <Form.Item label="Telefone" name="phone">
                  <Input disabled prefix={<PhoneOutlined />} />
                </Form.Item>
              </Col>
              <Col xs={12}>
                <Form.Item label="E-mail" name="email">
                  <Input disabled prefix={<MailOutlined />} />
                </Form.Item>
              </Col>
              <Col xs={12}>
                <Form.Item
                  label="Motivo"
                  name="motivo"
                  rules={[{ required: true, message: 'Informe o motivo' }]}
                >
                  <Select
                    options={(Object.keys(MOTIVO_LABEL) as CrcPendencyType[]).map((m) => ({
                      label: MOTIVO_LABEL[m],
                      value: m,
                    }))}
                  />
                </Form.Item>
              </Col>
              <Col xs={12}>
                <Form.Item
                  label="Prioridade"
                  name="prioridade"
                  rules={[{ required: true, message: 'Informe prioridade' }]}
                >
                  <Select
                    options={(Object.keys(PRIORITY_COLORS) as CrcPendencyPriority[]).map((p) => ({
                      label: PRIORITY_COLORS[p].label,
                      value: p,
                    }))}
                  />
                </Form.Item>
              </Col>
              <Col xs={12}>
                <Form.Item
                  label="Situação"
                  name="situacao"
                  rules={[{ required: true, message: 'Informe situação' }]}
                >
                  <Select
                    options={([
                      { label: 'Aberta', value: 'ABERTA' },
                      { label: 'Resolvida', value: 'RESOLVIDA' },
                      { label: 'Ignorada', value: 'IGNORADA' },
                    ] as { label: string; value: CrcPendencyStatus }[])}
                  />
                </Form.Item>
              </Col>
              <Col xs={12}>
                <Form.Item label="Dias sem contato" name="dias_contato">
                  <Input type="number" disabled />
                </Form.Item>
              </Col>
              <Col xs={24}>
                <Form.Item label="Último contato" name="ultimo_contato">
                  <DatePicker
                    style={{ width: '100%' }}
                    format="DD/MM/YYYY"
                    placeholder="Data do último contato"
                  />
                </Form.Item>
              </Col>
              <Col xs={24}>
                <Form.Item label="Ação recomendada" name="acao_recomendada">
                  <Input.TextArea rows={3} placeholder="Ação recomendada para resolver esta pendência..." />
                </Form.Item>
              </Col>
              <Col xs={24}>
                <Form.Item
                  label="Observações / Resolução"
                  name="observacoes"
                  rules={[{ required: false }]}
                >
                  <Input.TextArea rows={5} placeholder="Digite observações para esta pendência..." />
                </Form.Item>
              </Col>
            </Row>
            <Divider />
            <Space direction="vertical" style={{ width: '100%' }} size={8}>
              <ATitle level={5}>Ações rápidas para este registro</ATitle>
              <Row gutter={8}>
                <Col xs={12}>
                  <AButton
                    block
                    icon={<WhatsAppOutlined />}
                    style={{ background: '#25D366', borderColor: '#25D366', color: '#fff' }}
                    onClick={() => quickWhatsApp(selectedPendency)}
                  >
                    WhatsApp
                  </AButton>
                </Col>
                <Col xs={12}>
                  <AButton block type="primary" icon={<PhoneOutlined />} onClick={() => quickCall(selectedPendency)}>
                    Ligar
                  </AButton>
                </Col>
                <Col xs={12} style={{ marginTop: 8 }}>
                  <AButton block icon={<MailOutlined />} onClick={() => quickEmail(selectedPendency)}>
                    E-mail
                  </AButton>
                </Col>
                <Col xs={12} style={{ marginTop: 8 }}>
                  <AButton
                    block
                    icon={<CalendarOutlined />}
                    style={{ background: '#722ed1', borderColor: '#722ed1', color: '#fff' }}
                    onClick={() => quickSchedule(selectedPendency)}
                  >
                    Agendar Retorno
                  </AButton>
                </Col>
              </Row>
            </Space>
          </Form>
        )}
      </Drawer>
    </PageLayout>
  );
};

export default CrcPendencies;
